import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Container,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import {
  extractTitle,
  extractLinks,
  findFileByHref,
} from "../utils/fileAnalysis";

interface LocationState {
  parentFile: File;
  allFiles: File[];
}

interface FileNode {
  file: File;
  title: string;
  links: Array<{
    href: string;
    title: string;
    text: string;
  }>;
  children: FileNode[];
  isLoading: boolean;
}

interface SelectedFile {
  file: File;
  order: number;
  path: string[];
}

interface FileOccurrence {
  node: FileNode;
  depth: number;
  path: string[];
  parent: FileNode | null;
}

const Traverse: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { parentFile, allFiles } = (location.state as LocationState) || {};

  const [rootNode, setRootNode] = useState<FileNode | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Map<string, SelectedFile>>(
    new Map()
  );
  const [isInitializing, setIsInitializing] = useState(true);

  // Redirect if no files provided
  useEffect(() => {
    if (!parentFile || !allFiles) {
      navigate("/file-select");
    }
  }, [parentFile, allFiles, navigate]);

  // Initialize root node
  useEffect(() => {
    const initializeRoot = async () => {
      if (!parentFile || !allFiles) return;

      setIsInitializing(true);
      const title = await extractTitle(parentFile);
      const links = await extractLinks(parentFile, allFiles);

      setRootNode({
        file: parentFile,
        title,
        links,
        children: [],
        isLoading: false,
      });

      // Don't auto-select any files
      setSelectedFiles(new Map());

      setIsInitializing(false);
    };

    initializeRoot();
  }, [parentFile, allFiles]);

  // Find all occurrences of a file in the current tree
  const findFileOccurrences = (
    fileName: string,
    node: FileNode,
    depth: number = 0,
    path: string[] = [],
    parent: FileNode | null = null,
    occurrences: FileOccurrence[] = []
  ): FileOccurrence[] => {
    const currentPath = [...path, node.title];

    if (node.file.name === fileName) {
      occurrences.push({
        node,
        depth,
        path: currentPath,
        parent,
      });
    }

    node.children.forEach((child) => {
      findFileOccurrences(
        fileName,
        child,
        depth + 1,
        currentPath,
        node,
        occurrences
      );
    });

    return occurrences;
  };

  // Check if a file would be at the highest level if added at a specific depth
  const wouldBeHighestLevel = (
    fileName: string,
    potentialDepth: number
  ): boolean => {
    if (!rootNode) return true;

    const currentOccurrences = findFileOccurrences(fileName, rootNode);

    // If no current occurrences, this would be the first (and highest)
    if (currentOccurrences.length === 0) return true;

    // Check if this depth would be higher than all current occurrences
    const minCurrentDepth = Math.min(...currentOccurrences.map((o) => o.depth));
    return potentialDepth < minCurrentDepth;
  };

  // Get the current highest level occurrence of a file
  const getHighestLevelOccurrence = (
    fileName: string
  ): FileOccurrence | null => {
    if (!rootNode) return null;

    const occurrences = findFileOccurrences(fileName, rootNode);
    if (occurrences.length === 0) return null;

    return occurrences.reduce((highest, current) =>
      current.depth < highest.depth ? current : highest
    );
  };

  const removeNodeAndChildren = (
    node: FileNode,
    selected: Map<string, SelectedFile>
  ) => {
    // Remove this node
    selected.delete(node.file.name);

    // Recursively remove all children
    node.children.forEach((child) => {
      removeNodeAndChildren(child, selected);
    });
  };

  const toggleFileSelection = async (node: FileNode, path: string[]) => {
    const newSelected = new Map(selectedFiles);

    if (newSelected.has(node.file.name)) {
      // Deselecting - remove this node and all its children
      removeNodeAndChildren(node, newSelected);
    } else {
      // Selecting - add to selection and load children if not already loaded
      newSelected.set(node.file.name, {
        file: node.file,
        order: 0, // Will be calculated based on tree position
        path: [...path],
      });

      // Load children if this is the first time selecting and we haven't loaded them yet
      if (
        node.links.length > 0 &&
        node.children.length === 0 &&
        !node.isLoading &&
        allFiles
      ) {
        node.isLoading = true;
        setRootNode({ ...rootNode! });

        // Load children in the original order from links
        const newChildren: FileNode[] = [];
        const childrenMap = new Map<string, FileNode>();

        // First pass: create all valid child nodes
        for (const link of node.links) {
          const childFile = findFileByHref(link.href, allFiles);

          if (childFile) {
            // Check if we should skip this file
            const currentDepth = path.length + 1;
            const existingOccurrence = getHighestLevelOccurrence(
              childFile.name
            );

            // Skip if:
            // 1. File already exists at a higher level
            // 2. File already exists at the same level (sibling)
            if (
              existingOccurrence &&
              existingOccurrence.depth <= currentDepth
            ) {
              continue;
            }

            const title = await extractTitle(childFile);
            const links = await extractLinks(childFile, allFiles);

            const childNode: FileNode = {
              file: childFile,
              title,
              links,
              children: [],
              isLoading: false,
            };

            childrenMap.set(childFile.name, childNode);
          }
        }

        // Second pass: add children in the original link order
        for (const link of node.links) {
          const childFile = findFileByHref(link.href, allFiles);
          if (childFile && childrenMap.has(childFile.name)) {
            newChildren.push(childrenMap.get(childFile.name)!);
            childrenMap.delete(childFile.name); // Ensure no duplicates
          }
        }

        node.children = newChildren;
        node.isLoading = false;
        setRootNode({ ...rootNode! });
      }
    }

    setSelectedFiles(newSelected);
  };

  const getTreeOrderedFiles = (
    node: FileNode,
    path: string[] = [],
    result: SelectedFile[] = []
  ): SelectedFile[] => {
    const currentPath = [...path, node.title];

    // If this node is selected, add it to results
    if (selectedFiles.has(node.file.name)) {
      const selectedFile = selectedFiles.get(node.file.name)!;
      result.push({
        file: selectedFile.file,
        order: result.length + 1,
        path: currentPath,
      });
    }

    // Recursively process children in tree order
    node.children.forEach((child) => {
      getTreeOrderedFiles(child, currentPath, result);
    });

    return result;
  };

  const handleSelectAllChildren = (node: FileNode, path: string[]) => {
    const newSelected = new Map(selectedFiles);

    // Select all children
    node.children.forEach((child) => {
      const childPath = [...path, child.title];
      if (!newSelected.has(child.file.name)) {
        newSelected.set(child.file.name, {
          file: child.file,
          order: 0,
          path: childPath,
        });
      }
    });

    setSelectedFiles(newSelected);
  };

  const handleDeselectAllChildren = (node: FileNode) => {
    const newSelected = new Map(selectedFiles);

    // Deselect all children and their descendants
    node.children.forEach((child) => {
      removeNodeAndChildren(child, newSelected);
    });

    setSelectedFiles(newSelected);
  };

  const renderNode = (
    node: FileNode,
    depth: number = 0,
    path: string[] = []
  ): React.ReactElement => {
    const currentPath = [...path, node.title];
    const isSelected = selectedFiles.has(node.file.name);

    return (
      <Box key={`${node.file.name}_${path.join("/")}`} sx={{ ml: depth * 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            py: 0.5,
            backgroundColor: isSelected ? "action.hover" : "transparent",
            borderRadius: 1,
          }}
        >
          <Checkbox
            checked={isSelected}
            onChange={() => toggleFileSelection(node, currentPath)}
            size="small"
            sx={{ py: 0 }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.875rem",
              fontWeight: isSelected ? 500 : 400,
            }}
          >
            {node.title}
            {node.links.length > 0 && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.75rem", ml: 1 }}
              >
                ({node.links.length})
              </Typography>
            )}
          </Typography>
          {selectedFiles.has(node.file.name) && rootNode && (
            <Chip
              label={
                getTreeOrderedFiles(rootNode).findIndex(
                  (sf) => sf.file.name === node.file.name
                ) + 1
              }
              size="small"
              sx={{ ml: 1, height: 20, fontSize: "0.75rem" }}
            />
          )}
          {node.isLoading && <CircularProgress size={14} sx={{ ml: 1 }} />}
        </Box>

        {/* Show select/deselect all buttons if there are more than 5 children */}
        {isSelected && node.children.length > 5 && (
          <Box sx={{ ml: (depth + 1) * 2, my: 1 }}>
            <Button
              size="small"
              onClick={() => handleSelectAllChildren(node, currentPath)}
              sx={{
                fontSize: "0.75rem",
                py: 0.25,
                px: 1,
                minHeight: "auto",
                textTransform: "none",
                mr: 1,
              }}
              variant="outlined"
            >
              Select all
            </Button>
            <Button
              size="small"
              onClick={() => handleDeselectAllChildren(node)}
              sx={{
                fontSize: "0.75rem",
                py: 0.25,
                px: 1,
                minHeight: "auto",
                textTransform: "none",
              }}
              variant="outlined"
            >
              Deselect all
            </Button>
          </Box>
        )}

        {/* Show all links in their original order */}
        {isSelected &&
          allFiles &&
          rootNode &&
          node.links.map((link) => {
            const file = findFileByHref(link.href, allFiles);
            if (!file) return null;

            // Check if this file is already a loaded child
            const existingChild = node.children.find(
              (child) => child.file.name === file.name
            );

            // If it's already a child, render it normally
            if (existingChild) {
              return renderNode(existingChild, depth + 1, currentPath);
            }

            // Otherwise, check if it exists elsewhere in the tree
            const willBeHighest = wouldBeHighestLevel(file.name, depth + 1);
            const currentHighest = getHighestLevelOccurrence(file.name);

            // If it doesn't exist anywhere else, don't show it (it will be loaded when parent is selected)
            if (!currentHighest) return null;

            // Show the disabled/moveable duplicate
            return (
              <Box
                key={`disabled_${file.name}`}
                sx={{
                  ml: (depth + 1) * 2,
                  display: "flex",
                  alignItems: "center",
                  py: 0.5,
                  opacity: willBeHighest ? 1 : 0.5,
                }}
              >
                <Tooltip
                  title={
                    willBeHighest
                      ? `This page currently exists at level ${
                          currentHighest.depth + 1
                        }. Clicking here would move it to level ${
                          depth + 2
                        } (higher priority).`
                      : `This page already exists at level ${
                          currentHighest.depth + 1
                        }, which is higher priority than level ${depth + 2}.`
                  }
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={false}
                      disabled={!willBeHighest}
                      size="small"
                      sx={{ py: 0 }}
                      onClick={async () => {
                        if (willBeHighest && rootNode) {
                          // Remove the current occurrence
                          const currentOccurrence = getHighestLevelOccurrence(
                            file.name
                          );
                          if (currentOccurrence && currentOccurrence.parent) {
                            // Remove from parent's children
                            currentOccurrence.parent.children =
                              currentOccurrence.parent.children.filter(
                                (child) => child.file.name !== file.name
                              );

                            // Also remove from selected files if it was selected
                            const newSelected = new Map(selectedFiles);
                            removeNodeAndChildren(
                              currentOccurrence.node,
                              newSelected
                            );
                            setSelectedFiles(newSelected);
                          }

                          // Add as child of current node
                          const title = await extractTitle(file);
                          const links = await extractLinks(file, allFiles);

                          const newChild: FileNode = {
                            file: file,
                            title,
                            links,
                            children: [],
                            isLoading: false,
                          };

                          node.children.push(newChild);
                          setRootNode({ ...rootNode });
                        }
                      }}
                    />
                    <LockIcon
                      sx={{
                        fontSize: 16,
                        mr: 0.5,
                        color: willBeHighest
                          ? "warning.main"
                          : "text.secondary",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.875rem",
                        color: willBeHighest
                          ? "text.primary"
                          : "text.secondary",
                        textDecoration: willBeHighest ? "none" : "line-through",
                        fontStyle: willBeHighest ? "italic" : "normal",
                        cursor: willBeHighest ? "pointer" : "default",
                      }}
                    >
                      {link.title || link.text}
                      {willBeHighest && " (click to move here)"}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            );
          })}
      </Box>
    );
  };

  const handleBack = () => {
    navigate("/file-select");
  };

  const handleNext = () => {
    if (rootNode) {
      const orderedFiles = getTreeOrderedFiles(rootNode);

      navigate("/export", {
        state: {
          parentFile,
          selectedFiles: orderedFiles,
          allFiles,
        },
      });
    }
  };

  if (!parentFile || !allFiles || isInitializing) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Select Pages for Export
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Check the boxes to include pages in your export. Selecting a page will
          automatically load its children. Duplicate pages can be moved to
          higher levels by clicking on them when they show "(click to move
          here)".
        </Typography>

        <Stack spacing={3} sx={{ mt: 3 }}>
          {/* File Tree */}
          <Card>
            <CardContent>{rootNode && renderNode(rootNode)}</CardContent>
          </Card>

          {selectedFiles.size === 0 ? (
            <Alert severity="info">
              No pages selected. Select pages from the structure above.
            </Alert>
          ) : (
            <Alert severity="success">
              {selectedFiles.size} pages selected.
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              size="large"
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForwardIcon />}
              disabled={selectedFiles.size === 0}
              size="large"
            >
              Continue to Export
            </Button>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
};

export default Traverse;
